import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Entity} from '../../entity/entity';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EstimatedReleaseScheduleService} from '../../services/estimated-release-schedule.service';
import {StringUtils} from '../../utils/string-utils';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import {EstimatedReleaseSchedule} from '../../model/estimated-release-schedule';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {UpdateEstimatedScheduleDialogComponent} from '../update-estimated-schedule-dialog/update-estimated-schedule-dialog.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';


@Component({
    selector: 'app-entity-estimated-schedule-dialog',
    templateUrl: './entity-estimated-schedule-dialog.component.html',
    styleUrls: ['./entity-estimated-schedule-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, MatSuffix, MatTooltip, MatButton, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow]
})
export class EntityEstimatedScheduleDialogComponent implements OnInit {
  private readonly service = inject(EstimatedReleaseScheduleService);
  private dialogRef = inject<MatDialogRef<EntityEstimatedScheduleDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  private readonly scheduleData = signal<EstimatedReleaseSchedule[]>(undefined!);
  readonly entity: Entity;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly schedules: MatTableDataSource<EstimatedReleaseSchedule> = new MatTableDataSource<EstimatedReleaseSchedule>();
  warningMessage: string;
  displayedColumns: string[] = ['edit', 'delete', 'quantity', 'date', 'comments'];

  constructor() {
    const data = this.data;

    this.entity = JSON.parse(JSON.stringify(data.entity));
    this.warningMessage = data.warningMessage;
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.service.getEntityEstimatedSchedules(this.entity.id.entityId).subscribe({
      next: (result: EstimatedReleaseSchedule[]) => {
        this.scheduleData.set(result);
        this.updateScheduleTable();
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)
    });
  }

  private updateScheduleTable(): void {
    this.schedules.data = this.scheduleData();
    this.schedules.sort = this.sort;
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  cancel(): void {
    this.dialogRef.close();
  }

  changesDisabled(): boolean {
    return this.isBusy() || this.remainingQuantity() !== 0;
  }

  save(): void {
    if (!this.changesDisabled()) {
      this.isBusy.set(true);
      this.service.saveSchedules(this.scheduleData()).subscribe({
        next: () => this.dialogRef.close(),
        error: (error) => this.handleError(error)
      });
    }
  }

  estimatedQuantity(): number {
    if (this.schedules && this.schedules.data && this.schedules.data.length > 0) {
      return this.schedules.data.map(schedule => schedule.quantity).reduce((accumulator, currentValue) => accumulator + currentValue);
    }
    return 0;
  }

  remainingQuantity(): number {
    return this.entity.unitQuantity - this.estimatedQuantity();
  }

  editSchedule(schedule: EstimatedReleaseSchedule): void {
    this.showUpdateDialog(false, schedule);
  }

  deleteSchedule(schedule: EstimatedReleaseSchedule): void {
    this.scheduleData.update(current => current.filter(item => item !== schedule));
    this.updateScheduleTable();
  }

  addSchedule(): void {
    const schedule: EstimatedReleaseSchedule = new EstimatedReleaseSchedule();
    schedule.entityId = this.entity.id.entityId;
    schedule.shipDate = (new Date()).toISOString().substring(0, 10);
    schedule.quantity = 0;
    schedule.id = 0;
    this.showUpdateDialog(true, schedule);
  }

  showUpdateDialog(isNew: boolean, schedule: EstimatedReleaseSchedule): void {
    const maxQuantity = isNew ? this.remainingQuantity() : this.remainingQuantity() + schedule.quantity;
    const updateDialog = this.dialog.open(UpdateEstimatedScheduleDialogComponent, {
      width: '780px;',
      height: '440px',
      data: {schedule: schedule, maxQuantity: maxQuantity}
    });
    updateDialog.afterClosed().subscribe({
      next: (result: any) => {
        if (result && result as EstimatedReleaseSchedule) {
          if (isNew) {
            this.scheduleData.update(current => [...current, result]);
          }
          this.updateScheduleTable();
        }
      }
    });
  }

}
